import React from "react";
import { Route, Switch, Redirect } from "wouter";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AgencyManagement from "@/pages/AgencyManagement";
import CounselorManagement from "@/pages/CounselorManagement";
import AgencyCounselors from "@/pages/AgencyCounselors";
import CollegeManagement from "@/pages/CollegeManagement";
import StudentManagement from "@/pages/StudentManagement";
import CalendarManagement from "@/pages/CalendarManagement";
import CounselorResources from "@/pages/CounselorResources";
import CounselorResourceEdit from "@/pages/CounselorResourceEdit";
import CmsCollections from "@/pages/CmsCollections";
import CmsCollectionBuilder from "@/pages/CmsCollectionBuilder";
import CmsItems from "@/pages/CmsItems";
import CmsItemEdit from "@/pages/CmsItemEdit";
import WebsiteContent from "@/pages/WebsiteContent";
import WebsiteContentEditor from "@/pages/WebsiteContentEditor";
import SessionHistory from "@/pages/SessionHistory";
import AiSessions from "@/pages/AiSessions";
import MbaReadiness from "@/pages/MbaReadiness";
import Campaigns from "@/pages/Campaigns";
import Templates from "@/pages/Templates";
import AdminManagement from "@/pages/AdminManagement";
import AiGuidelines from "@/pages/AiGuidelines";
import AiCosts from "@/pages/AiCosts";
import Guide from "@/pages/Guide";
import Impersonate from "@/pages/Impersonate";
import ImpersonationBanner from "@/components/impersonation/ImpersonationBanner";
import Layout from "@/components/Layout";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { MODULE_KEYS } from "@/lib/adminModules";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "super_admin" | "admin" | string;
  /**
   * Module this route belongs to. Hiding the sidebar link is not a permission —
   * without this check, typing the URL still opens the page.
   */
  requiredModule?: string;
  /**
   * An agency admin only ever reaches their own agency's counselors —
   * every other route (including Dashboard) redirects them there. This is
   * the one route that opts in.
   */
  allowAgencyAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredModule,
  allowAgencyAdmin,
}) => {
  const { isAuthenticated, isLoading, isSuperAdmin, isAgencyAdmin, hasModule } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  if (isAgencyAdmin && !allowAgencyAdmin) {
    return <Redirect to="/agency-counselors" />;
  }

  if (requiredRole === "super_admin") {
    if (!isSuperAdmin) {
      return <Redirect to="/dashboard" />;
    }
  }

  if (requiredModule && !hasModule(requiredModule)) {
    return <Redirect to="/dashboard" />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, isAgencyAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Redirect to={isAgencyAdmin ? "/agency-counselors" : "/dashboard"} />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Switch>
      {/* Deliberately not wrapped in PublicRoute: the super admin's own session
       * (localStorage) would otherwise redirect them away before the one-time
       * code is exchanged. */}
      <Route path="/impersonate">
        <Impersonate />
      </Route>

      <Route path="/login">
        <PublicRoute>
          <Login />
        </PublicRoute>
      </Route>

      <Route path="/dashboard">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>

      <Route path="/agency-counselors">
        <ProtectedRoute allowAgencyAdmin>
          <AgencyCounselors />
        </ProtectedRoute>
      </Route>

      <Route path="/guide">
        <ProtectedRoute>
          <Guide />
        </ProtectedRoute>
      </Route>

      <Route path="/admin-management">
        <ProtectedRoute requiredRole="super_admin">
          <AdminManagement />
        </ProtectedRoute>
      </Route>

      <Route path="/ai-guidelines">
        <ProtectedRoute requiredRole="super_admin">
          <AiGuidelines />
        </ProtectedRoute>
      </Route>

      <Route path="/ai-costs">
        <ProtectedRoute requiredRole="super_admin">
          <AiCosts />
        </ProtectedRoute>
      </Route>

      <Route path="/agencies">
        <ProtectedRoute requiredModule={MODULE_KEYS.AGENCIES}>
          <AgencyManagement />
        </ProtectedRoute>
      </Route>

      <Route path="/counselors">
        <ProtectedRoute requiredModule={MODULE_KEYS.COUNSELORS}>
          <CounselorManagement />
        </ProtectedRoute>
      </Route>

      <Route path="/colleges">
        <ProtectedRoute requiredModule={MODULE_KEYS.COLLEGES}>
          <CollegeManagement />
        </ProtectedRoute>
      </Route>

      <Route path="/students">
        <ProtectedRoute requiredModule={MODULE_KEYS.STUDENTS}>
          <StudentManagement />
        </ProtectedRoute>
      </Route>

      <Route path="/calendar-management">
        <ProtectedRoute requiredModule={MODULE_KEYS.CALENDAR}>
          <CalendarManagement />
        </ProtectedRoute>
      </Route>

      <Route path="/counselor-resources/create">
        <ProtectedRoute requiredModule={MODULE_KEYS.COUNSELOR_RESOURCES}>
          <CounselorResourceEdit />
        </ProtectedRoute>
      </Route>

      <Route path="/counselor-resources/edit/:id">
        <ProtectedRoute requiredModule={MODULE_KEYS.COUNSELOR_RESOURCES}>
          <CounselorResourceEdit />
        </ProtectedRoute>
      </Route>

      <Route path="/counselor-resources">
        <ProtectedRoute requiredModule={MODULE_KEYS.COUNSELOR_RESOURCES}>
          <CounselorResources />
        </ProtectedRoute>
      </Route>

      {/* Generic CMS — order matters: the specific /cms/collections/* routes
       * must come before the catch-all /cms/:collectionKey. */}
      <Route path="/cms">
        <ProtectedRoute requiredModule={MODULE_KEYS.CMS_COLLECTIONS}>
          <CmsCollections />
        </ProtectedRoute>
      </Route>

      <Route path="/cms/collections/new">
        <ProtectedRoute requiredRole="super_admin">
          <CmsCollectionBuilder />
        </ProtectedRoute>
      </Route>

      <Route path="/cms/collections/:id/edit">
        <ProtectedRoute requiredRole="super_admin">
          <CmsCollectionBuilder />
        </ProtectedRoute>
      </Route>

      <Route path="/cms/:collectionKey/items/new">
        <ProtectedRoute requiredModule={MODULE_KEYS.CMS_COLLECTIONS}>
          <CmsItemEdit />
        </ProtectedRoute>
      </Route>

      <Route path="/cms/:collectionKey/items/:id/edit">
        <ProtectedRoute requiredModule={MODULE_KEYS.CMS_COLLECTIONS}>
          <CmsItemEdit />
        </ProtectedRoute>
      </Route>

      <Route path="/cms/:collectionKey">
        <ProtectedRoute requiredModule={MODULE_KEYS.CMS_COLLECTIONS}>
          <CmsItems />
        </ProtectedRoute>
      </Route>

      {/* Super-admin-only website page content editor — order matters here
       * too: the list route must come before the /:slug catch-all. */}
      <Route path="/website-content">
        <ProtectedRoute requiredRole="super_admin">
          <WebsiteContent />
        </ProtectedRoute>
      </Route>

      <Route path="/website-content/:slug">
        <ProtectedRoute requiredRole="super_admin">
          <WebsiteContentEditor />
        </ProtectedRoute>
      </Route>

      <Route path="/sessions">
        <ProtectedRoute requiredModule={MODULE_KEYS.SESSIONS}>
          <SessionHistory />
        </ProtectedRoute>
      </Route>

      <Route path="/ai-sessions">
        <ProtectedRoute requiredModule={MODULE_KEYS.AI_SESSIONS}>
          <AiSessions />
        </ProtectedRoute>
      </Route>

      <Route path="/mba-readiness">
        <ProtectedRoute requiredModule={MODULE_KEYS.AI_SESSIONS}>
          <MbaReadiness />
        </ProtectedRoute>
      </Route>

      <Route path="/campaigns">
        <ProtectedRoute requiredModule={MODULE_KEYS.CAMPAIGNS}>
          <Campaigns />
        </ProtectedRoute>
      </Route>

      <Route path="/templates">
        <ProtectedRoute requiredModule={MODULE_KEYS.TEMPLATES}>
          <Templates />
        </ProtectedRoute>
      </Route>

      <Route path="/">
        <Redirect to="/dashboard" />
      </Route>

      {/* 404 fallback */}
      <Route>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
            <p className="text-gray-600 mb-4">Page not found</p>
            <a href="/dashboard" className="text-primary hover:underline">
              Go to Dashboard
            </a>
          </div>
        </div>
      </Route>
    </Switch>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50">
        <AppRoutes />
        <ImpersonationBanner />
      </div>
    </AuthProvider>
  );
};

export default App;
